import { Combobox, InputBase, useCombobox } from '@mantine/core'
import { Icon } from '../icon'
import styled, { css } from 'styled-components'
import { FilterProps } from './filter.types'

const FILTER_DIVIDER_VALUE = '__filter-divider__'

export const StyledFilterWrapper = styled.div<{ $compact?: boolean }>`
  ${({ theme, $compact }) => `
  position: relative;
  ${$compact ? 'display: inline-block;' : ''}

  .mantine-InputBase-wrapper {
    background-color: white !important;
    ${$compact ? 'width: fit-content;' : ''}
  }

  .mantine-InputBase-input {
    border: none;
    background-color: white;
    border-radius: ${$compact ? theme.borderRadii.large : '4px'};
    display: ${$compact ? 'inline-flex' : 'flex'};
    align-items: center;
    ${
      $compact
        ? `
      cursor: pointer;
      padding: 0 ${theme.sizes.xs};
      height: auto;
      width: fit-content;
      color: black;
      font-size: ${theme.fontSizes.default};
      font-weight: ${theme.fontWeights.medium};
      user-select: none;

      *, p, span, svg {
        cursor: pointer;
        user-select: none;
      }
    `
        : ''
    }

    &:hover {
      background-color: ${theme.colors.gray70};
    }

    > span {
      display: inline-block;
      margin: ${$compact ? '0' : '5px 0'};
    }
  }

  .mantine-Combobox-option {
    padding: 0px;
    word-break: normal;
    ${$compact ? 'cursor: pointer;' : ''}
  }

  ${
    $compact
      ? `
    .mantine-Combobox-options {
      padding: ${theme.sizes.xxs};
      gap: 0;
    }

    .mantine-Combobox-dropdown {
      box-shadow: ${theme.boxShadows.small};
      background-color: white;
      width: auto;
      min-width: fit-content;
      max-width: fit-content;
    }
  `
      : ''
  }
`}
`

const StyledIconWrapper = styled.div<{ $compact?: boolean }>`
  ${({ theme, $compact }) =>
    $compact
      ? css`
          cursor: pointer;
          pointer-events: auto;
          padding-left: ${theme.sizes.xxs};
          display: inline-flex;
          align-items: center;
          justify-content: center;

          svg {
            width: 1rem;
            height: 1rem;
          }
        `
      : css`
          right: 1rem;
          cursor: pointer;
          pointer-events: auto;
          padding-left: 5px;
          display: flex;
          justify-content: center;
          svg {
            width: 1rem;
            height: 1rem;
          }
        `}
`

const StyledFilterLabelText = styled.span<{
  disabled?: boolean
  $compact?: boolean
}>`
  ${({ theme, disabled, $compact }) => `
  display: flex;
  align-items: center;
  padding: ${$compact ? `6px ${theme.sizes.xs}` : '0.5rem'};
  border-radius: ${$compact ? theme.borderRadii.medium : '4px'};
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  font-size: ${$compact ? theme.fontSizes.default : '14px'};
  color: ${disabled ? 'gray' : 'black'};
  background-color: white;
  margin: ${$compact ? '0' : '5px'};
  ${$compact ? 'white-space: nowrap; user-select: none;' : ''}

  &:hover {
    background-color: ${disabled ? 'white' : theme.colors.gray70};
  }
`}
`

const Divider = styled.hr`
  ${({ theme }) => `
    border: none;
    border-top: 1px solid ${theme.colors.gray90};
    margin: ${theme.sizes.xxs} 0;
  `}
`

/**
 * Phrasing content only — this renders inside the trigger `<button>`. The colour is explicit rather
 * than inherited so the label cannot pick up whatever the host app styles bare elements with.
 */
const StyledLabelText = styled.span`
  ${({ theme }) => `
    display: inline-block;
    color: ${theme.semantic.contentPrimary};
  `}
`

export const Filter = ({
  label,
  options,
  selectedValue,
  onChange,
  testId,
  position,
  disableSelectedOption = true,
  compact = false,
}: FilterProps) => {
  const selectedLabel =
    options.find((option) => option.value === selectedValue)?.label ?? label

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })
  const open = combobox.dropdownOpened

  const handleToggleDropdown = () => combobox.toggleDropdown()

  const isOptionDisabled = (
    item: FilterProps['options'][number],
    index: number
  ) => {
    if (!disableSelectedOption) return false
    if (index === 0) return item.value === '' && !selectedValue
    return item.value === selectedValue
  }

  return (
    <StyledFilterWrapper $compact={compact}>
      <Combobox
        store={combobox}
        withinPortal={false}
        position={position}
        onOptionSubmit={(val) => {
          if (val === FILTER_DIVIDER_VALUE) return
          const selectedOption = options.find((option) => option.value === val)

          onChange(selectedOption?.value || '')
          combobox.closeDropdown()
        }}
      >
        <Combobox.Target>
          <InputBase
            component="button"
            type="button"
            onClick={handleToggleDropdown}
            rightSectionPointerEvents="none"
            data-testid={testId}
            style={
              compact
                ? undefined
                : {
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'black',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    display: 'inline-block',
                  }
            }
          >
            <StyledLabelText>
              {selectedValue ? selectedLabel : label}
            </StyledLabelText>
            <StyledIconWrapper $compact={compact}>
              <Icon
                name={open ? 'chevron-up' : 'chevron-down'}
                size={8}
                color="black"
              />
            </StyledIconWrapper>
          </InputBase>
        </Combobox.Target>

        <Combobox.Dropdown
          style={
            compact
              ? undefined
              : {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  backgroundColor: '#ffffff',
                  zIndex: 1000,
                  position: 'absolute',
                  width: 'auto',
                  minWidth: 'fit-content',
                  maxWidth: 'fit-content',
                }
          }
        >
          <Combobox.Options>
            {options.map((item, index) => {
              if (item.value === FILTER_DIVIDER_VALUE) {
                return <Divider key="filter-divider" />
              }

              const disabled = isOptionDisabled(item, index)

              return (
                <Combobox.Option
                  value={item.value}
                  key={item.value}
                  disabled={disabled}
                  style={compact ? undefined : { margin: '0.5rem 0' }}
                >
                  <StyledFilterLabelText
                    disabled={disabled}
                    $compact={compact}
                    data-testid={`filter-${item.value}`}
                  >
                    {item.label}
                  </StyledFilterLabelText>
                </Combobox.Option>
              )
            })}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </StyledFilterWrapper>
  )
}

export const FILTER_DIVIDER = FILTER_DIVIDER_VALUE
